import { Currency, CurrencyAmount } from '@kodiak-finance/sdk-core'
import { ColumnMinGap } from 'components/AccountDetails'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Area, Bar, Cell, ComposedChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { ThemeContext } from 'styled-components'
import { ThemedText } from 'theme'
import { formatAmount, formatDollarAmount } from 'utils/formatDollarAmt'

import { FlexRowCenter } from '../styleds'
import * as S from './styleds'
import {
  filterDataByTimeframe,
  selectDataKey,
  selectTimeFormat,
  selectXAxisTimeFormat,
  SnapshotType,
} from './utils/chartHelpers'

type TvlViewType = {
  unit: 'both' | 'token0' | 'token1'
  timeframe: '7' | '30' | '90' | 'max'
  period: '1' | '2'
  snapshots: SnapshotType[]
  currencyA?: Currency
  currencyB?: Currency
}

type CustomTickLabelProps = {
  x?: number
  y?: number
  payload?: any
  axisType?: 'xAxis' | 'yAxis'
  theme: any
}

type CustomTooltipProps = {
  active?: boolean
  payload?: any[]
  label?: string
  theme?: any
  xAxisFormatter: any
  activeToken: 'both' | string
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({
  active,
  payload,
  label,
  theme,
  xAxisFormatter,
  activeToken,
}) => {
  if (active && payload && payload.length) {
    const tvlValue = payload[0].value
    const netFlowValue = payload[0].payload?.netFlow || 0

    const netFlowColor = netFlowValue > 0 ? theme.success500 : netFlowValue < 0 ? theme.error500 : theme.textSecondary

    const netFlowAbs = Math.abs(netFlowValue)
    const netFlowFormatted = activeToken === 'both' ? formatDollarAmount(netFlowAbs) : formatAmount(netFlowAbs)
    const netFlowSign = netFlowValue > 0 ? '+' : netFlowValue < 0 ? '-' : ''

    return (
      <S.StrategyTooltipFlex>
        <ThemedText.DeprecatedBody fontSize="14px" fontWeight="700" lineHeight="20px" color={theme.textPrimary}>
          {xAxisFormatter(payload[0].payload.timestamp)}
        </ThemedText.DeprecatedBody>
        <ColumnMinGap style={{ gap: 0 }}>
          <FlexRowCenter>
            <ThemedText.DeprecatedBody fontSize="12px" fontWeight="400" lineHeight="20px" color={theme.textSecondary}>
              {activeToken === 'both' ? 'Total Value Locked (USD):' : `Amount (${activeToken})`}
            </ThemedText.DeprecatedBody>
            <ThemedText.DeprecatedBody fontSize="12px" fontWeight="500" lineHeight="20px" color={theme.textPrimary}>
              {activeToken === 'both' ? formatDollarAmount(tvlValue) : formatAmount(tvlValue)}
            </ThemedText.DeprecatedBody>
          </FlexRowCenter>
          <FlexRowCenter>
            <ThemedText.DeprecatedBody fontSize="12px" fontWeight="400" lineHeight="20px" color={theme.textSecondary}>
              Daily Change:
            </ThemedText.DeprecatedBody>
            <ThemedText.DeprecatedBody fontSize="12px" fontWeight="500" lineHeight="20px" color={netFlowColor}>
              {netFlowSign}
              {netFlowFormatted}
            </ThemedText.DeprecatedBody>
          </FlexRowCenter>
        </ColumnMinGap>
      </S.StrategyTooltipFlex>
    )
  }
  return null
}

const TvlView: React.FC<TvlViewType> = ({ unit, timeframe, snapshots, currencyA, currencyB }) => {
  const theme = useContext(ThemeContext)
  const [filteredData, setFilteredData] = useState<SnapshotType[] | []>([])

  // const tempSnapshots = TempVaultSnapshots.data.kodiakVaultDailySnapshots
  //   .map((snapshot) => ({
  //     ...snapshot,
  //     timestamp: Number(snapshot.timestamp), // Convert timestamp to number for sorting
  //   }))
  //   .sort((a, b) => a.timestamp - b.timestamp)

  // useEffect(() => {
  //   const filteredData = filterDataByTimeframe(snapshots, timeframe)
  //   setFilteredData(filteredData)
  // }, [timeframe])

  const currencyWrappedA = currencyA?.wrapped
  const currencyWrappedB = currencyB?.wrapped

  const activeToken = unit === 'both' ? 'both' : unit === 'token0' ? currencyWrappedA?.symbol : currencyWrappedB?.symbol

  // REAL DATA

  useEffect(() => {
    if (snapshots && snapshots.length > 0) {
      // Create a copy of the snapshots array
      const snapshotsCopy = snapshots.map((snapshot) => ({
        ...snapshot,
        timestamp: Number(snapshot.timestamp),
        tvlToken0: CurrencyAmount.fromRawAmount(currencyWrappedA!, Number(snapshot._token0Amount)).toSignificant(
          currencyA?.decimals
        ),
        tvlToken1: CurrencyAmount.fromRawAmount(currencyWrappedB!, Number(snapshot._token1Amount)).toSignificant(
          currencyB?.decimals
        ),
      }))

      // Sort the copied array by timestamp in ascending order
      const sortedSnapshots = snapshotsCopy.sort((a, b) => a.timestamp - b.timestamp)

      // Filter the sorted snapshots based on the selected timeframe
      const newFilteredData = filterDataByTimeframe(sortedSnapshots, timeframe)

      // Update the state with the filtered data
      setFilteredData(newFilteredData)
    }
  }, [timeframe, snapshots])

  const combinedData = useMemo(() => {
    if (!filteredData || filteredData.length === 0) return []

    // Get one extra day for calculating first day's change
    const firstFilteredTimestamp = filteredData[0].timestamp
    const sortedSnapshots = snapshots
      .map((snapshot) => ({
        ...snapshot,
        timestamp: Number(snapshot.timestamp),
      }))
      .sort((a, b) => a.timestamp - b.timestamp)

    const firstFilteredIndex = sortedSnapshots.findIndex((s) => Number(s.timestamp) === Number(firstFilteredTimestamp))
    const hasPreviousDay = firstFilteredIndex > 0
    const dataWithPreviousDay = hasPreviousDay
      ? [sortedSnapshots[firstFilteredIndex - 1], ...filteredData]
      : filteredData

    const result = dataWithPreviousDay.map((snapshot, index) => {
      const tvlValue = selectDataKey(snapshot, unit)

      let netFlow = 0
      let isPositive = true

      if (index > 0) {
        const previousValue = selectDataKey(dataWithPreviousDay[index - 1], unit)
        netFlow = tvlValue - previousValue
        isPositive = netFlow >= 0
      }

      return {
        timestamp: snapshot.timestamp,
        tvl: tvlValue,
        netFlow,
        isPositive,
      }
    })

    return hasPreviousDay ? result.slice(1) : result
  }, [filteredData, unit, snapshots])

  const tooltipXAxisFormatter = selectTimeFormat(timeframe)
  const xAxisFormatter = selectXAxisTimeFormat(timeframe)

  const CustomTickLabel: React.FC<CustomTickLabelProps> = ({ x, y, payload, axisType, theme }) => {
    const value =
      axisType === 'xAxis'
        ? xAxisFormatter(payload.value) // Format as time for x-axis
        : formatDollarAmount(payload.value) // Format as currency for y-axis

    return (
      <text
        x={x}
        y={y}
        dy={axisType === 'xAxis' ? 10 : 0}
        dx={axisType === 'xAxis' ? 0 : -14}
        textAnchor="middle"
        fill={theme.textSecondary}
        fontSize={12}
      >
        {value}
      </text>
    )
  }

  const CustomAssetTickLabel: React.FC<CustomTickLabelProps> = ({ x, y, payload, axisType, theme }) => {
    const value = formatAmount(payload.value) // Format as currency for y-axis

    return (
      <text x={x} y={y} dy={0} dx={-14} textAnchor="middle" fill={theme.textSecondary} fontSize={12}>
        {value}
      </text>
    )
  }

  const CustomNetFlowTickLabel: React.FC<CustomTickLabelProps> = ({ x, y, payload, theme }) => {
    const value = payload.value
    const absValue = Math.abs(value)

    const displayValue = absValue < 0.01 ? '$0' : `${value < 0 ? '-' : ''}${formatDollarAmount(absValue)}`

    return (
      <text x={x} y={y} dy={0} dx={-14} textAnchor="middle" fill={theme.textSecondary} fontSize={12}>
        {displayValue}
      </text>
    )
  }

  const CustomXLabel = unit === 'both' ? CustomTickLabel : CustomAssetTickLabel

  // const tvlValues = TempVaultSnapshots.data.kodiakVaultDailySnapshots.map((snapshot) => {
  //   switch (unit) {
  //     case 'token0':
  //       return parseFloat(snapshot.vault._token0.totalValueLockedUSD)
  //     case 'token1':
  //       return parseFloat(snapshot.vault._token1.totalValueLockedUSD)
  //     default: // for 'both'
  //       return parseFloat(snapshot.totalValueLockedUSD)
  //   }
  // })

  // const averageTVL = tvlValues.reduce((a, b) => a + b, 0) / tvlValues.length
  // const maxTVL = Math.max(...tvlValues)
  // const minTVL = Math.max(Math.min(...tvlValues) - maxTVL * 0.1, 0) // Subtracts 10% of maxTVL from minTVL

  return (
    <S.GraphFlex style={{ overflow: 'hidden', flexDirection: 'column', height: 'auto', padding: '0' }}>
      {/* TVL Chart */}
      <ResponsiveContainer width="100%" height={182} minWidth="100%">
        <ComposedChart data={combinedData} margin={{ top: 24, right: 8, left: -8, bottom: 0 }} syncId="tvlSync">
          <defs>
            <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#3083DC" stopOpacity={0.8} />
              <stop offset="95%" stopColor="#3083DC" stopOpacity={0} />
            </linearGradient>
          </defs>

          <XAxis
            dataKey="timestamp"
            stroke="white"
            tickLine={false}
            axisLine={false}
            tick={<CustomTickLabel axisType="xAxis" theme={theme} />}
            minTickGap={5}
            tickFormatter={xAxisFormatter}
            scale="point"
          />
          <YAxis
            dataKey="tvl"
            stroke="white"
            tickLine={false}
            axisLine={false}
            tick={<CustomXLabel axisType="yAxis" theme={theme} />}
            minTickGap={5}
          />
          <Tooltip
            content={<CustomTooltip theme={theme} xAxisFormatter={tooltipXAxisFormatter} activeToken={activeToken!} />}
          />
          <Area type="monotone" dataKey="tvl" stroke="#82ca9e74" fillOpacity={1} fill="url(#colorUv)" />
        </ComposedChart>
      </ResponsiveContainer>

      {/* Net Flow Bar Chart */}
      <ResponsiveContainer width="100%" height={180} minWidth="100%">
        <ComposedChart data={combinedData} margin={{ top: 8, right: 8, left: -8, bottom: 0 }} syncId="tvlSync">
          <defs>
            <clipPath id="barChartClip">
              <rect x="52" y="0" width="calc(100% - 60px)" height="100%" />
            </clipPath>
          </defs>
          <XAxis dataKey="timestamp" hide scale="point" />
          <YAxis
            dataKey="netFlow"
            width={60}
            stroke="white"
            tickLine={false}
            axisLine={false}
            tick={<CustomNetFlowTickLabel theme={theme} />}
            tickCount={10}
          />
          {/* eslint-disable-next-line react/prop-types */}
          <ReferenceLine y={0} stroke={theme.textSecondary} strokeOpacity={0.3} strokeWidth={1} strokeDasharray="3 3" />
          <Tooltip content={<></>} />
          <Bar dataKey="netFlow" maxBarSize={60} clipPath="url(#barChartClip)">
            {combinedData.map((entry, index) => (
              // eslint-disable-next-line react/prop-types
              <Cell key={`cell-${index}`} fill={entry.isPositive ? theme.success500 : theme.error500} />
            ))}
          </Bar>
          <Area type="monotone" dataKey="netFlow" stroke="transparent" fill="transparent" />
        </ComposedChart>
      </ResponsiveContainer>
    </S.GraphFlex>
  )
}

export default TvlView
